import { useRef, useState } from 'react'
import { FileUp } from 'lucide-react'
import { useToast } from '../../../admin/primitives.js'
import { Checkbox } from '../../../admin/form.js'
import { useConfirm } from '../../../shell/confirm.js'
import { fmtNombre } from '../commun.js'
import { ecrireSonde } from './ecrire.js'
import { LigneRetour, Vide, type Retour } from './elements.js'
import {
  LIBELLE_ACTION_IMPORT, problemeCsv, estResultatImport, exclusionsValides, grouperImport, nombreAppliques,
  type ResultatImport,
} from './modele.js'

/**
 * ONGLET « IMPORT TECH_LC » (21/10/2026) — la cartographie des caméras du
 * tableur TECH_LC, en deux temps :
 *   1. aperçu (`mode: "preview"`) : RIEN n'est écrit ; créations, mises à jour,
 *      rattachements, remplacements, CONFLITS (jamais appliqués) et remarques ;
 *   2. confirmation (`mode: "apply"`, même `preview_hash`, `exclude` éventuel).
 * Le registre a bougé entre-temps (409 PREVIEW_OUTDATED) : l'aperçu est relu
 * tout seul, les exclusions encore valables sont gardées, rien n'est appliqué.
 */

const NIVEAU: Record<string, string> = { error: 'Erreur', warning: 'Attention', info: 'Information' }

export function OngletImport(props: { peutGerer: boolean; onCompte: () => void }) {
  const toast = useToast()
  const confirm = useConfirm()
  const fichierRef = useRef<HTMLInputElement>(null)
  const [csv, setCsv] = useState<{ nom: string; texte: string } | null>(null)
  const [apercu, setApercu] = useState<ResultatImport | null>(null)
  const [applique, setApplique] = useState<ResultatImport | null>(null)
  const [exclus, setExclus] = useState<Set<string>>(new Set())
  const [envoi, setEnvoi] = useState<'apercu' | 'application' | null>(null)
  const [retour, setRetour] = useState<Retour>(null)

  const lireApercu = async (f: { nom: string; texte: string }, garder: Set<string> = new Set()): Promise<ResultatImport | null> => {
    setEnvoi('apercu')
    const e = await ecrireSonde('POST', 'import/techlc', { csv: f.texte, filename: f.nom.slice(0, 200), mode: 'preview' }, { rien: 'rien n’a été lu' })
    setEnvoi(null)
    if (!e.ok || !estResultatImport(e.data)) {
      setApercu(null)
      setRetour({ ton: 'ko', texte: e.ok ? 'Réponse inattendue de Sonde : aucun aperçu affiché plutôt qu’un aperçu faux.' : e.message })
      return null
    }
    setApercu(e.data)
    setExclus(exclusionsValides(garder, e.data.items))
    return e.data
  }

  const choisir = async (file: File | undefined) => {
    setRetour(null); setApercu(null); setApplique(null); setExclus(new Set())
    if (!file) return
    const texte = await file.text()
    const probleme = problemeCsv(texte)
    if (probleme) { setCsv(null); setRetour({ ton: 'ko', texte: probleme }); return }
    const f = { nom: file.name, texte }
    setCsv(f)
    await lireApercu(f)
  }

  const appliquer = async () => {
    if (!csv || !apercu) return
    const n = nombreAppliques(apercu.items, exclus)
    if (n === 0) return
    const ok = await confirm({
      title: `Appliquer ${n} changement${n > 1 ? 's' : ''} au registre ?`, variant: 'sensitive', confirmLabel: 'Appliquer',
      message: `Les postes, identifiants et liens retenus entrent au registre de Sonde. ${apercu.counts.conflict > 0 ? `Les ${apercu.counts.conflict} conflit${apercu.counts.conflict > 1 ? 's' : ''} ne sont PAS appliqués : ils se règlent à la main.` : 'Aucun conflit.'}`,
      details: [
        { label: 'Fichier', value: csv.nom },
        ...(exclus.size > 0 ? [{ label: 'Écartés à la main', value: String(exclus.size) }] : []),
      ],
    })
    if (!ok) return
    setEnvoi('application'); setRetour(null)
    const e = await ecrireSonde('POST', 'import/techlc', {
      csv: csv.texte, filename: csv.nom.slice(0, 200), mode: 'apply', preview_hash: apercu.preview_hash,
      ...(exclus.size > 0 ? { exclude: [...exclus] } : {}),
    }, { rien: 'rien n’a été appliqué' })
    setEnvoi(null)
    if (!e.ok && e.code === 'PREVIEW_OUTDATED') {
      const nouveau = await lireApercu(csv, exclus)
      setRetour({ ton: 'ko', texte: `Le registre a changé depuis l’aperçu : rien n’a été appliqué. ${nouveau ? 'Voici le nouvel aperçu : relisez-le avant de confirmer.' : ''}` })
      return
    }
    if (!e.ok || !estResultatImport(e.data)) { setRetour({ ton: 'ko', texte: e.ok ? 'Réponse inattendue de Sonde : relisez le registre.' : e.message }); return }
    setApplique(e.data); setApercu(null)
    const a = e.data.applied
    toast('ok', 'Import TECH_LC appliqué.')
    setRetour({ ton: 'ok', texte: a
      ? `Appliqué : ${a.devices_created} poste${a.devices_created > 1 ? 's' : ''} créé${a.devices_created > 1 ? 's' : ''}, ${a.devices_updated} mis à jour, ${a.identifiers_attached} identifiant${a.identifiers_attached > 1 ? 's' : ''} rattaché${a.identifiers_attached > 1 ? 's' : ''} (${a.identifiers_closed} clos), ${a.links_created} lien${a.links_created > 1 ? 's' : ''} créé${a.links_created > 1 ? 's' : ''}.${a.zones_seeded.length ? ` Salles créées : ${a.zones_seeded.join(', ')}.` : ''}`
      : 'Import appliqué.' })
    props.onCompte()
  }

  const vue = apercu ?? applique
  const groupes = vue ? grouperImport(vue.items) : []
  const aAppliquer = apercu ? nombreAppliques(apercu.items, exclus) : 0

  return (
    <section className="sv-rgs-onglet" aria-label="Import TECH_LC">
      <div className="lc-panel sv-rgs-import">
        <div className="lc-panel__body">
          <p className="sv-rgs-intro">
            Le tableur TECH_LC (export CSV, séparateur « ; ») décrit chaque caméra : nom, MAC, switch et port, NVR. Sonde compare d’abord
            avec le registre — <strong>rien n’est écrit à l’aperçu</strong> — puis applique ce que vous confirmez. Une MAC différente pour un
            poste existant = exemplaire remplacé (l’ancienne est close, l’historique suit le poste). Les conflits ne sont jamais appliqués.
          </p>
          {props.peutGerer ? (
            <div className="sv-rgs-import__choix">
              <input ref={fichierRef} type="file" accept=".csv,text/csv,text/plain" className="sv-rgs-fichier" aria-label="Fichier CSV TECH_LC"
                onChange={(e) => { void choisir(e.target.files?.[0]); e.target.value = '' }} />
              <button type="button" className="lc-btn lc-btn--primary" disabled={envoi !== null} onClick={() => fichierRef.current?.click()}>
                <FileUp size={15} aria-hidden="true" /> {envoi === 'apercu' ? 'Aperçu en cours…' : csv ? 'Choisir un autre fichier' : 'Choisir le fichier CSV'}
              </button>
              {csv ? <span className="sv-rgs-aide">{csv.nom} · {fmtNombre(Math.round(csv.texte.length / 1024))} Kio</span> : <span className="sv-rgs-aide">590 Kio au plus.</span>}
            </div>
          ) : (
            <p className="sv-rgs-lecture-seule" role="note">Lecture seule : importer demande le droit « Gérer le registre de la supervision ».</p>
          )}
          <LigneRetour retour={retour} />
        </div>
      </div>

      {vue ? (
        <>
          <dl className="sv-rgs-chiffres">
            <div><dt>Lignes lues</dt><dd>{fmtNombre(vue.rows.parsed)} / {fmtNombre(vue.rows.total)}</dd></div>
            <div><dt>Lignes rejetées</dt><dd>{fmtNombre(vue.rows.rejected)}</dd></div>
            <div><dt>Postes créés</dt><dd>{fmtNombre(vue.counts.create_device)}</dd></div>
            <div><dt>Postes mis à jour</dt><dd>{fmtNombre(vue.counts.update_device)}</dd></div>
            <div><dt>Identifiants rattachés</dt><dd>{fmtNombre(vue.counts.attach_identifier)}</dd></div>
            <div><dt>Remplacements</dt><dd>{fmtNombre(vue.counts.replace_identifier + vue.counts.replace_link)}</dd></div>
            <div><dt>Liens créés</dt><dd>{fmtNombre(vue.counts.create_link)}</dd></div>
            <div className={vue.counts.conflict > 0 ? 'sv-rgs-chiffres--conflit' : undefined}><dt>Conflits (jamais appliqués)</dt><dd>{fmtNombre(vue.counts.conflict)}</dd></div>
            <div><dt>Inchangés</dt><dd>{fmtNombre(vue.counts.unchanged)}</dd></div>
          </dl>

          {vue.issues.length > 0 ? (
            <section className="lc-panel sv-rgs-groupe" aria-label="Remarques">
              <header className="lc-panel__head"><h3 className="lc-section-title">Remarques <span className="sv-rgs-compte">{vue.issues.length}</span></h3></header>
              <ul className="sv-rgs-items sv-rgs-groupe__corps">
                {vue.issues.map((i, n) => (
                  <li key={n} className={`sv-rgs-item sv-rgs-item--${i.level}`}>
                    <span className="sv-rgs-item__corps">
                      <span>{i.message_fr}</span>
                      <span className="sv-rgs-meta">
                        <span>{NIVEAU[i.level] ?? i.level}</span>
                        {i.line !== null ? <span>Ligne {i.line}</span> : null}
                        {i.fiche !== null ? <span>Fiche {i.fiche}</span> : null}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {groupes.length === 0 ? <Vide titre="Rien à changer : le registre correspond déjà au tableur." /> : null}
          {groupes.map((g) => (
            <section key={g.action} className={`lc-panel sv-rgs-groupe${g.action === 'conflict' ? ' sv-rgs-groupe--conflit' : ''}`} aria-label={LIBELLE_ACTION_IMPORT[g.action]}>
              <header className="lc-panel__head">
                <h3 className="lc-section-title">{LIBELLE_ACTION_IMPORT[g.action]} <span className="sv-rgs-compte">{g.items.length}</span></h3>
                {g.action === 'conflict' ? <span className="sv-rgs-aide">Jamais appliqués : à régler à la main sur la fiche du poste.</span> : null}
              </header>
              <ul className="sv-rgs-items sv-rgs-groupe__corps">
                {g.items.map((i) => (
                  <li key={i.id} className="sv-rgs-item">
                    {apercu && props.peutGerer && i.applicable && i.action !== 'conflict' ? (
                      <Checkbox checked={!exclus.has(i.id)} ariaLabel={`Appliquer : ${i.summary_fr}`}
                        onChange={(v) => setExclus((s) => { const n = new Set(s); if (v) n.delete(i.id); else n.add(i.id); return n })} />
                    ) : null}
                    <span className="sv-rgs-item__corps">
                      <span>{i.summary_fr}</span>
                      <span className="sv-rgs-meta">
                        <span className="sv-mono">{i.uid}</span>
                        {i.line !== null ? <span>Ligne {i.line}</span> : null}
                        {!i.applicable && i.reason_fr ? <span>{i.reason_fr}</span> : null}
                        {apercu && exclus.has(i.id) ? <span>Écarté : ne sera pas appliqué</span> : null}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ))}

          {apercu && props.peutGerer ? (
            <div className="sv-rgs-pied sv-rgs-pied--page">
              <span className="sv-rgs-aide">{aAppliquer} changement{aAppliquer > 1 ? 's' : ''} retenu{aAppliquer > 1 ? 's' : ''}{apercu.counts.conflict > 0 ? ` · ${apercu.counts.conflict} conflit${apercu.counts.conflict > 1 ? 's' : ''} laissé${apercu.counts.conflict > 1 ? 's' : ''} de côté` : ''}</span>
              <button type="button" className="lc-btn lc-btn--primary" disabled={envoi !== null || aAppliquer === 0} onClick={() => void appliquer()}>
                {envoi === 'application' ? 'Application…' : `Appliquer ${aAppliquer} changement${aAppliquer > 1 ? 's' : ''}`}
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  )
}
